import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageHeader from "components/PageLayout";
import { ArrowLeft, Send, Plus, Trash2, Building2, ChevronDown, ChevronRight } from "lucide-react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import SubmitConfirmation from "components/MidApplications/SubmitConfirmation";
import { useAuth } from "utilities/auth";
import { useToast } from "components/Toast";

export default function MidApplicationCreate() {
  const navigate = useNavigate();
  const { id: companyId } = useParams();
  const { user } = useAuth();
  const { toast } = useToast();

  const [processorIds, setProcessorIds] = useState([""]);
  const [merchants, setMerchants] = useState([]);
  const [registration, setRegistration] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState([]);
  const [showMatches, setShowMatches] = useState(true);
  const [selectedDbaId, setSelectedDbaId] = useState("");

  // --- Fetch processors from DB
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [merchantsRes, registrationRes] = await Promise.all([
          makeApiRequest(
            "get",
            apiRoutes.getProcessors,
            { id: user.id, accessToken: user.accessToken },
            companyId
          ),
          makeApiRequest(
            "get",
            apiRoutes.getApplication(companyId),
            { id: user.id, accessToken: user.accessToken },
            companyId
          ),
        ]);
        setMerchants(merchantsRes.data?.data || []);
        setRegistration(registrationRes.data || null);

        // Fetch match recommendations
        try {
          const matchRes = await makeApiRequest(
            "get",
            apiRoutes.getProcessorMatches(companyId),
            { id: user.id, accessToken: user.accessToken },
            companyId
          );
          setMatches(matchRes.data?.data?.matches || []);
        } catch {
          setMatches([]);
        }
      } catch (err) {
        console.error("[MidApplicationCreate] Failed to load data", err);
        toast({
          status: "destructive",
          title: "Failed to load data",
          description: err.response?.data?.error || "Could not load processors or registration.",
        });
      } finally {
        setLoading(false);
      }
    };

    if (companyId) {
      fetchData();
    }
  }, [user?.id, user?.accessToken, companyId]);

  function handleCancel() {
    navigate(-1);
  }

  // --- Merchant selection handlers
  function handleMerchantChange(index, value) {
    const updated = [...processorIds];
    updated[index] = value;
    setProcessorIds(updated);
  }

  function addMerchantRow() {
    setProcessorIds([...processorIds, ""]);
  }

  function removeMerchantRow(index) {
    setProcessorIds(processorIds.filter((_, i) => i !== index));
  }

  // --- Submit
  // SOUND-643: this creates one application PER SELECTED PROCESSOR in a single
  // click, and those records are the trigger for outbound processor
  // submissions. It used to POST straight off the button. The click now only
  // validates and opens the confirmation gate; submitApplications does the write.
  function handleSubmitClick() {
    const cleaned = processorIds.filter((id) => id && id.trim() !== "");
    if (cleaned.length === 0) {
      toast({
        status: "warning",
        title: "Missing processor",
        description: "Please select at least one processor.",
      });
      return;
    }
    setConfirmOpen(true);
  }

  async function submitApplications() {
    setSaving(true);

    try {
      const cleaned = processorIds.filter((id) => id && id.trim() !== "");

      // Resolve DBA or fall back to parent corp
      const dbas = registration?.dbas || [];
      const selectedDba = dbas.find((d) => d.dbaId === selectedDbaId);
      const dbaId = selectedDba?.dbaId || null;
      const dbaName = selectedDba?.dbaName || null;
      const companyName =
        dbaName ||
        registration?.businessInfo?.legalName ||
        "Unknown Company";

      // Build payload with processor + DBA info
      const merchantObjects = cleaned.map((id) => {
        const processor = merchants.find((m) => m.processorId === id);
        return {
          // SOUND-654: the API stores this as processorId. It always WAS one —
          // it is resolved with `m.processorId === id` on the line above.
          processorId: id,
          companyName,
          dbaId,
          dbaName,
        };
      });

      const payload = { merchants: merchantObjects };

      const res = await makeApiRequest(
        "post",
        apiRoutes.createMidApplication,
        { id: user.id, accessToken: user.accessToken },
        companyId,
        payload
      );

      toast({
        status: "success",
        title: "Application(s) created",
        description: `Created ${res.data.data.length} application(s).`,
        action: {
          label: "View applications",
          onClick: () => navigate("/mid-applications"),
        },
      });

      setConfirmOpen(false);
      navigate("/mid-applications");
    } catch (err) {
      // Leave the dialog open so the operator sees which submission failed
      // rather than being returned to a form with no context.
      console.error("[MidApplicationCreate] Failed to save application(s)", err);
      toast({
        status: "destructive",
        title: "Failed to create application(s)",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Create Application(s)"
        subtitle="Generate new MID applications from processor records."
        actions={
          <div className="flex gap-2">
            <button className="btn btn-ghost" onClick={handleCancel}>
              <ArrowLeft className="h-4 w-4" />
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSubmitClick}
              disabled={saving}
            >
              <Send className="h-4 w-4" />
              {saving ? "Submitting..." : "Submit"}
            </button>
          </div>
        }
      />

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand" />
        </div>
      )}

      {/* Registration Summary */}
      {!loading && registration && (
        <div className="surface rounded-xl ring-1 ring-border p-6 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-brand/20 rounded-lg">
              <Building2 className="w-5 h-5 text-brand" />
            </div>
            <h2 className="text-lg font-semibold">
              {registration.businessInfo?.legalName || "Unknown Company"}
            </h2>
          </div>
          {registration.dbas?.length > 0 ? (
            <p className="text-sm text-muted-foreground">
              {registration.dbas.length} DBA{registration.dbas.length !== 1 ? "s" : ""}:{" "}
              {registration.dbas.map((d) => d.dbaName).join(", ")}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              DBA: {registration.businessInfo?.dbaName || "N/A"}
            </p>
          )}
          <p className="text-sm text-muted-foreground">
            Created:{" "}
            {new Date(registration.createdAt).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })}
          </p>
        </div>
      )}

      {!loading && <div className="container py-6 space-y-6">
        {/* DBA Selection */}
        <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
          <h2 className="text-lg font-semibold">DBA / Business Entity</h2>
          <p className="text-sm text-muted-foreground">
            Select which DBA this application is for.{" "}
            {(!registration?.dbas || registration.dbas.length === 0) &&
              "No DBAs found — the parent company will be used."}
          </p>
          {registration?.dbas?.length > 0 ? (
            <select
              className="select select-bordered w-full max-w-md"
              value={selectedDbaId}
              onChange={(e) => setSelectedDbaId(e.target.value)}
            >
              <option value="">
                {registration.businessInfo?.legalName || "Parent Company"} (Parent Corp)
              </option>
              {registration.dbas.map((dba) => (
                <option key={dba.dbaId} value={dba.dbaId}>
                  {dba.dbaName}
                </option>
              ))}
            </select>
          ) : (
            <p className="text-sm font-medium text-foreground">
              {registration?.businessInfo?.legalName || "Unknown Company"}
            </p>
          )}
        </section>

        {/* Match Recommendations */}
        {matches.length > 0 && (
          <section className="surface rounded-xl ring-1 ring-border p-6 space-y-3">
            <button
              className="flex items-center gap-2 w-full text-left"
              onClick={() => setShowMatches(!showMatches)}
            >
              {showMatches ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
              <h2 className="text-lg font-semibold">Recommended Processors</h2>
              <span className="text-sm text-muted-foreground ml-auto">
                {matches.length} match{matches.length !== 1 ? "es" : ""}
              </span>
            </button>

            {showMatches && (
              <div className="space-y-3 pt-2">
                {matches.slice(0, 5).map((m) => (
                  <div
                    key={m.processorId}
                    className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                      processorIds.includes(m.processorId)
                        ? "border-brand bg-brand/5"
                        : "border-border hover:border-brand/50"
                    }`}
                    onClick={() => {
                      // Add processor to first empty slot, or append a new row
                      const emptyIndex = processorIds.findIndex((id) => !id);
                      if (emptyIndex >= 0) {
                        handleMerchantChange(emptyIndex, m.processorId);
                      } else if (!processorIds.includes(m.processorId)) {
                        setProcessorIds([...processorIds, m.processorId]);
                      }
                    }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-foreground">
                        {m.processorName}
                      </span>
                      <span
                        className={`text-sm font-semibold px-2 py-0.5 rounded-full ${
                          m.score >= 80
                            ? "bg-success/10 text-success"
                            : m.score >= 50
                            ? "bg-warning/10 text-warning"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {m.score}/{m.maxScore}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {m.criteria.map((c) => (
                        <span
                          key={c.name}
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            c.matched
                              ? "bg-success/10 text-success"
                              : "bg-destructive/10 text-destructive"
                          }`}
                          title={c.reason}
                        >
                          {c.name}: {c.matched ? "Pass" : "Fail"}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Processor Selection */}
        <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
          <h2 className="text-lg font-semibold">Processors</h2>
          <p className="text-sm text-muted-foreground">
            Select one or more processors to create MID applications.
          </p>

          {processorIds.map((id, i) => (
            <div key={i} className="flex gap-2 items-center">
              <select
                className="select select-bordered flex-1"
                value={id}
                onChange={(e) => handleMerchantChange(i, e.target.value)}
              >
                <option value="">Select a processor</option>
                {merchants?.map((m) => (
                  <option key={m.processorId} value={m.processorId}>
                    {m.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => removeMerchantRow(i)}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}

          <button
            type="button"
            className="btn btn-ghost inline-flex items-center gap-2"
            onClick={addMerchantRow}
          >
            <Plus className="h-4 w-4" />
            Add Processor
          </button>
        </section>
      </div>}

      <SubmitConfirmation
        open={confirmOpen}
        submitting={saving}
        onConfirm={submitApplications}
        onCancel={() => setConfirmOpen(false)}
        processorIds={processorIds}
        merchants={merchants}
        registration={registration}
        selectedDbaId={selectedDbaId}
      />
    </div>
  );
}
